// ⚠️如果项目需要兼容ie11，则需要引入下面两行polyfill
// These must be the first lines in src/index.tsx
// import 'react-app-polyfill/ie11';
// import 'react-app-polyfill/stable';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { DevTools } from 'jotai-devtools';
import { Spin } from '@fuxi/eevee-ui';
import { configResponsive } from '@fuxi/eevee-hooks';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import relativeTime from 'dayjs/plugin/relativeTime';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/browser';
import { EeveeIconContext } from 'common/context/EeveeIconContext';
import CustomIndicator from '@/components/CustomIndicator';
import store from '@/store';
import { BlankLayout } from './layout';
import { getEnv } from './utils';
import { Breakpoints } from 'common/constants/breakpoints';

import 'uno.css';
import 'virtual:uno.css';
import 'antd/dist/reset.css';
import 'common/styles/util.css';
import 'common/styles/global.css';
import 'common/styles/extra-font-family.css';
import 'common/styles/extension-variables.css';
import 'common/styles/antd-override/index.css';
import 'jotai-devtools/styles.css';

import './styles/tiangong-global.css';

configResponsive(Breakpoints);
const { PROD: isProd, mode } = getEnv();
if (isProd) {
  Sentry.init({
    dsn: 'https://54e2c92b37ba468b953ff7693ba05623@sentry.fuxi.netease.com/33',
    // @ts-ignore
    integrations: [new BrowserTracing()],
    environment: mode,
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.2,
  });
  //getUserLoginInfo从localStorage取的值，这个方法好像只在对外版本生效，暂时注释
  // const { user } = getUserLoginInfo();
  // Sentry.setUser({ user });
}

dayjs.extend(utc);
dayjs.extend(timezone);

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(relativeTime);
dayjs.locale('zh-cn');

Spin.setDefaultIndicator(<CustomIndicator />);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 0,
      // networkMode 默认 'online'：onlineManager 判定离线时，query 会被静默停在
      // fetchStatus: 'paused' —— 不发请求、不报错，组件只会拿到空数据，命中缓存的
      // 页面看起来还一切正常，排查起来极其隐蔽。
      // 而 onlineManager 回退到 navigator.onLine，Safari 下会误报 false（睡醒、切网
      // 后尤其常见），一误报就是整站 query 悄悄停摆。
      // 改为 'always' 保证请求一定发出：真离线时只是快速失败进入 error 态（可见、可重试），
      // 而不是无声无息地卡住；恢复联网仍由 refetchOnReconnect 兜底。
      networkMode: 'always',
    },
  },
});

const container = document.getElementById('root');
const root = createRoot(container!); // createRoot(container!) if you use TypeScript

root.render(
  // React18 后的严格模式会造成两次渲染的问题，开发阶段可以禁用
  // <React.StrictMode>
  <Provider store={store}>
    <EeveeIconContext>
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        <BlankLayout />
        {/* {import.meta.env.MODE !== 'prod' && <DevTools position="bottom-right" />} */}
      </QueryClientProvider>
    </EeveeIconContext>
  </Provider>,
  // </React.StrictMode>,
);
